import * as microsoftTeams from '@microsoft/teams-js';
import { captureException } from '@sentry/react';
import { useCallback, useEffect, useState } from 'react';

interface MsTeamsClient {
  isInitialized: boolean;
  getContext: () => Promise<microsoftTeams.app.Context | null>;
  getAuthToken: () => Promise<string | null>;
  error: Error | null;
  clientType: microsoftTeams.HostClientType | null;
}

/**
 * Hook to access Microsoft Teams SDK used for the integration of BEEM inside MS Teams as a app.
 */
const useMsTeamsClient = (): MsTeamsClient => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [clientType, setClientType] = useState<microsoftTeams.HostClientType | null>(null);

  // Initialize the Teams client
  const init = useCallback(async () => {
    if (!isInitialized) {
      // TODO: this solution works only for web. need another solution for mobile.
      // MS Teams embeds the app in an iframe; skip initialization entirely in top-level windows
      // if (window.self === window.top) {
      //   return;
      // }
      try {
        await microsoftTeams.app.initialize();
        const context = await microsoftTeams.app.getContext();
        setClientType(context.app.host.clientType);
        setIsInitialized(true);
        // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      } catch (initError: any) {
        if (initError.message !== 'Initialization Failed. No Parent window found.') {
          captureException(initError);
          setError(initError as Error);
        }
      }
    }
  }, [isInitialized]);

  // Get the Microsoft Teams context
  const getContext = useCallback(async (): Promise<microsoftTeams.app.Context | null> => {
    if (!isInitialized) {
      throw new Error('Teams not initialized!');
    }
    try {
      return await microsoftTeams.app.getContext();
    } catch (contextError) {
      captureException(contextError);
      setError(contextError as Error);
      return null;
    }
  }, [isInitialized]);

  // Get the Microsoft Teams auth token
  const getAuthToken = useCallback(async (): Promise<string | null> => {
    if (!isInitialized) {
      throw new Error('Teams not initialized!');
    }
    try {
      return await microsoftTeams.authentication.getAuthToken();
    } catch (authError) {
      captureException(authError);
      setError(authError as Error);
      return null;
    }
  }, [isInitialized]);

  // Ensure initialization happens when the hook is first used
  useEffect(() => {
    init();
  }, [init]);

  return {
    isInitialized,
    getContext,
    getAuthToken,
    error,
    clientType,
  };
};

export default useMsTeamsClient;
